import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, EyeOff, Eye, LogOut, Check, Ban, Pencil, Trash2, X } from "lucide-react";
import { api, formatData, setMeta } from "@/lib/museo";
import { toast } from "sonner";

const TOKEN_KEY = "museo_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [stats, setStats] = useState(null);
  const [promises, setPromises] = useState([]);
  const [query, setQuery] = useState("");
  const [filtro, setFiltro] = useState("tutti");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTesto, setEditTesto] = useState("");
  const [editFirma, setEditFirma] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMeta({ title: "Admin — Il Museo delle Promesse" });
  }, []);

  const headers = { "X-Admin-Token": token };

  const load = useCallback(
    async (q = "", f = "tutti") => {
      setLoading(true);
      try {
        const [s, p] = await Promise.all([
          api.get("/admin/stats", { headers: { "X-Admin-Token": token } }),
          api.get("/admin/promises", { params: { q, filtro: f }, headers: { "X-Admin-Token": token } }),
        ]);
        setStats(s.data);
        setPromises(p.data.items);
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Sessione scaduta");
        }
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    if (token) load("", filtro);
  }, [token, filtro, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
    } catch {
      toast.error("Password errata");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
    setPromises([]);
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/admin/promises/${id}/${action}`, {}, { headers });
      load(query, filtro);
    } catch {
      toast.error("Operazione fallita");
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditTesto(p.testo || "");
    setEditFirma(p.firma || "");
  };

  const saveEdit = async () => {
    if (!editTesto.trim()) {
      toast.error("Il testo non può essere vuoto");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/promises/${editing.id}/edit`, { testo: editTesto.trim(), firma: editFirma.trim() }, { headers });
      toast.success("Messaggio modificato");
      setEditing(null);
      load(query, filtro);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Modifica fallita");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (p) => {
    if (!window.confirm(`Eliminare definitivamente il messaggio ${p.numero_formatted || ""}? L'azione non è reversibile.`)) return;
    try {
      await api.delete(`/admin/promises/${p.id}`, { headers });
      toast.success("Messaggio eliminato");
      load(query, filtro);
    } catch {
      toast.error("Eliminazione fallita");
    }
  };

  if (!token) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6">
        <form onSubmit={handleLogin} className="w-full max-w-sm flex flex-col gap-8" data-testid="admin-login-form">
          <h1 className="font-serif text-3xl text-museo-text text-center">Pannello Admin</h1>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border-b border-museo-border text-museo-text text-lg py-3 focus:border-museo-accent focus:outline-none placeholder:text-museo-muted/40"
          />
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loggingIn || !password}
            className="w-full bg-museo-accent text-museo-bg px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-museo-text transition-all duration-500 disabled:opacity-30 flex items-center justify-center gap-2"
          >
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accedi"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-center justify-between mb-10">
        <h1 className="font-serif text-3xl md:text-4xl text-museo-text">Pannello Admin</h1>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="inline-flex items-center gap-2 text-sm text-museo-muted hover:text-museo-accent transition-colors uppercase tracking-[0.15em]"
        >
          <LogOut className="w-4 h-4" /> Esci
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-10" data-testid="admin-stats">
          <Stat label="Pagate reali" value={stats.totale_pagate} />
          <Stat label="Demo" value={stats.demo ?? 0} />
          <Stat label="Visibili" value={stats.visibili} />
          <Stat label="Nascoste" value={stats.nascoste} />
          <Stat label="Rifiutate" value={stats.rifiutate ?? 0} />
          <Stat label="In revisione" value={stats.review_required ?? 0} />
          <Stat label="Incasso lordo" value={`€ ${stats.incasso_lordo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-filters">
        {[
          ["tutti", "Tutti"],
          ["visibili", "Visibili"],
          ["nascoste", "Nascoste"],
          ["rifiutate", "Rifiutate"],
          ["review", "In revisione"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-filter-${key}`}
            onClick={() => setFiltro(key)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 rounded-none md:rounded-sm ${
              filtro === key
                ? "bg-museo-accent text-museo-bg border-museo-accent"
                : "border-museo-border text-museo-muted hover:border-museo-accent hover:text-museo-text"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mb-8 max-w-md">
        <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-museo-accent" />
        <input
          data-testid="admin-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(query.trim(), filtro)}
          placeholder="Cerca per testo o numero…"
          className="w-full bg-transparent border-b border-museo-border text-museo-text text-base py-3 pl-8 focus:border-museo-accent focus:outline-none placeholder:text-museo-muted/40"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-museo-accent animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto border border-museo-border rounded-none md:rounded-sm">
          <table className="w-full text-left text-sm" data-testid="admin-table">
            <thead className="border-b border-museo-border text-museo-muted uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3">Numero</th>
                <th className="px-4 py-3">Testo</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3">Motivo</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {promises.map((p) => (
                <tr key={p.id} className="border-b border-museo-border/50" data-testid={`admin-row-${p.numero ?? p.id}`}>
                  <td className="px-4 py-3 font-mono text-museo-accent whitespace-nowrap">{p.numero_formatted || "—"}</td>
                  <td className="px-4 py-3 text-museo-text font-serif max-w-xs">
                    {p.testo}
                    <span className="block text-xs italic text-museo-muted mt-1">{p.firma ? `— ${p.firma}` : "— Anonimo"}</span>
                  </td>
                  <td className="px-4 py-3 text-museo-muted whitespace-nowrap">{formatData(p.data)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatoBadge stato={p.stato} visibile={p.visibile} />
                  </td>
                  <td className="px-4 py-3 text-museo-muted font-mono text-xs">{p.block_reason || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-3 justify-end">
                      {p.visibile ? (
                        <ActionBtn testid={`admin-hide-${p.id}`} onClick={() => doAction(p.id, "hide")} icon={<EyeOff className="w-4 h-4" />} label="Nascondi" />
                      ) : (
                        <>
                          {p.stato === "hidden" && (
                            <ActionBtn testid={`admin-restore-${p.id}`} onClick={() => doAction(p.id, "restore")} icon={<Eye className="w-4 h-4" />} label="Ripristina" />
                          )}
                          {(p.stato === "review_required" || p.stato === "rejected") && (
                            <ActionBtn testid={`admin-approve-${p.id}`} onClick={() => doAction(p.id, "approve")} icon={<Check className="w-4 h-4" />} label="Approva" />
                          )}
                          {p.stato !== "rejected" && (
                            <ActionBtn testid={`admin-reject-${p.id}`} onClick={() => doAction(p.id, "reject")} icon={<Ban className="w-4 h-4" />} label="Rifiuta" />
                          )}
                        </>
                      )}
                      <ActionBtn testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} icon={<Pencil className="w-4 h-4" />} label="Modifica" />
                      <ActionBtn testid={`admin-delete-${p.id}`} onClick={() => doDelete(p)} icon={<Trash2 className="w-4 h-4" />} label="Elimina" danger />
                    </div>
                  </td>
                </tr>
              ))}
              {promises.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-museo-muted">Nessun messaggio.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-edit-modal">
          <div className="fixed inset-0 backdrop-blur-xl bg-museo-bg/85" onClick={() => setEditing(null)} />
          <div className="relative z-10 bg-museo-surface border border-museo-border p-8 max-w-lg w-full rounded-none md:rounded-sm">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-museo-muted hover:text-museo-accent">
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-serif text-2xl text-museo-text mb-1">Modifica messaggio</h2>
            <p className="font-mono text-xs text-museo-accent mb-6">{editing.numero_formatted}</p>
            <label className="block text-xs uppercase tracking-[0.2em] text-museo-muted mb-2">Testo</label>
            <textarea
              data-testid="admin-edit-testo"
              value={editTesto}
              onChange={(e) => setEditTesto(e.target.value.slice(0, 180))}
              rows={3}
              className="w-full bg-transparent border border-museo-border text-museo-text text-lg font-serif p-3 focus:border-museo-accent focus:outline-none resize-none rounded-none md:rounded-sm"
            />
            <div className="flex justify-end mt-1 mb-5">
              <span className="font-mono text-xs text-museo-muted">{editTesto.length} / 180</span>
            </div>
            <label className="block text-xs uppercase tracking-[0.2em] text-museo-muted mb-2">Firma (vuoto = Anonimo)</label>
            <input
              data-testid="admin-edit-firma"
              value={editFirma}
              onChange={(e) => setEditFirma(e.target.value.slice(0, 30))}
              className="w-full bg-transparent border border-museo-border text-museo-text text-base p-3 focus:border-museo-accent focus:outline-none rounded-none md:rounded-sm"
            />
            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setEditing(null)}
                className="flex-1 border border-museo-border text-museo-text px-6 py-3 text-sm uppercase tracking-[0.1em] hover:border-museo-accent transition-colors rounded-none md:rounded-sm"
              >
                Annulla
              </button>
              <button
                data-testid="admin-edit-save"
                onClick={saveEdit}
                disabled={saving}
                className="flex-1 bg-museo-accent text-museo-bg px-6 py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-museo-text transition-colors rounded-none md:rounded-sm disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-museo-surface border border-museo-border p-5 rounded-none md:rounded-sm">
    <p className="text-xs uppercase tracking-[0.15em] text-museo-muted mb-2">{label}</p>
    <p className="font-serif text-2xl text-museo-text">{value}</p>
  </div>
);

const STATO_LABELS = {
  visible: ["Visibile", "text-museo-accent"],
  hidden: ["Nascosta", "text-museo-error"],
  rejected: ["Rifiutata", "text-museo-error"],
  review_required: ["In revisione", "text-yellow-500"],
  pending_payment: ["In attesa", "text-museo-muted"],
};

const StatoBadge = ({ stato, visibile }) => {
  const key = stato || (visibile ? "visible" : "hidden");
  const [label, color] = STATO_LABELS[key] || [key, "text-museo-muted"];
  return <span className={`text-xs uppercase tracking-wider ${color}`}>{label}</span>;
};

const ActionBtn = ({ testid, onClick, icon, label, danger }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-1 text-xs uppercase tracking-wider transition-colors ${
      danger ? "text-museo-error/80 hover:text-museo-error" : "text-museo-muted hover:text-museo-accent"
    }`}
  >
    {icon} {label}
  </button>
);
